import { InfoCircleOutlined } from "@ant-design/icons";
import { isAdminUser } from "@shared/rbac";
import { useStoredUser } from "@shared/useStoredUser";
import { Grid, Popover, Typography, theme } from "antd";
import type { CSSProperties, ReactNode } from "react";
import { useCallback, useInsertionEffect, useMemo, useState } from "react";
import { usePageHelpViewed } from "../hooks/usePageHelpViewed";
import { injectPulseStyle } from "./injectPulseStyle";

interface PageHelpIconProps {
  path: string;
  description: ReactNode;
}

const PageHelpIcon = ({ path, description }: PageHelpIconProps) => {
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const user = useStoredUser();
  const isAdmin = isAdminUser(user);
  const { viewed, markViewed } = usePageHelpViewed(path);
  const [open, setOpen] = useState(false);

  useInsertionEffect(injectPulseStyle, []);

  const showPulse = !isAdmin && !viewed;

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setOpen(newOpen);
      if (newOpen && !viewed) {
        markViewed();
      }
    },
    [viewed, markViewed],
  );

  const iconStyle: CSSProperties = useMemo(
    () =>
      showPulse
        ? {
            fontSize: 16,
            color: token.colorPrimary,
            cursor: "pointer",
            animation: "acweb-page-help-pulse 2s ease-in-out infinite",
          }
        : {
            fontSize: 16,
            color: token.colorTextSecondary,
            cursor: "pointer",
            opacity: 0.45,
          },
    [showPulse, token.colorPrimary, token.colorTextSecondary],
  );

  const content = (
    <Typography.Text style={{ maxWidth: isMobile ? 280 : 360, display: "block" }}>
      {description}
    </Typography.Text>
  );

  return (
    <Popover
      content={content}
      trigger={["hover", "click"]}
      open={open}
      onOpenChange={handleOpenChange}
      placement={isMobile ? "bottom" : "bottomLeft"}
    >
      <InfoCircleOutlined
        style={iconStyle}
        data-tour-id="page-help-icon"
        aria-label="Описание страницы"
      />
    </Popover>
  );
};

export default PageHelpIcon;
